import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Button
} from '@mui/material';
import { CustomDialog } from '../CustomDialog';
import { useAllUsers } from '../../hooks/useAllUsers';
import { useEvent } from '../../hooks/useEvent';
import { Event, User } from '../../services/Types';
import { dialogButtons } from '../../global';

interface DialogTransferRequesterProps {
  eventData: Event;
  onSuccess: (newRequester: User) => void;
  onCancel: () => void;
}

export const DialogTransferRequester: React.FC<DialogTransferRequesterProps> = ({
  eventData,
  onSuccess,
  onCancel
}) => {
  const { users, programOfficeUsers } = useAllUsers();
  const { handleTransferEventRequester, updating } = useEvent();
  const [selectedUserId, setSelectedUserId] = useState<number>(0);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');

  const currentRequesterId = eventData?.creater?.id ?? eventData?.createrId ?? 0;

  const candidates = useMemo(() => {
    // Drafts may not have a program office yet, in which case the current
    // requester's office defines the pool. This mirrors the server-side check.
    const currentRequester = users.find(user => user.id === currentRequesterId);
    const programOfficeId = eventData?.programOffice?.id
      ?? eventData?.programOfficeId
      ?? currentRequester?.programOffice?.id
      ?? 0;

    return programOfficeUsers(programOfficeId)
      .filter(user => user.id !== currentRequesterId)
      .filter(user => user.approvalStatus === 'APPROVED')
      .sort((a, b) => `${a.lastName}${a.firstName}`.localeCompare(`${b.lastName}${b.firstName}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, eventData, currentRequesterId]);

  const selectedUser = candidates.find(user => user.id === selectedUserId);

  const handleChange = (event: SelectChangeEvent) => {
    setSelectedUserId(Number(event.target.value));
    setError('');
  };

  const handleContinue = () => {
    if (selectedUserId === 0) {
      setError('Please select a new requester');
      return;
    }
    setConfirming(true);
  };

  const handleConfirm = async () => {
    if (!selectedUser) return;
    const { success, message } = await handleTransferEventRequester(eventData.id, selectedUser.id);
    if (!success) {
      setConfirming(false);
      setError(message);
      return;
    }
    onSuccess(selectedUser);
  };

  if (confirming && selectedUser) {
    return (
      <CustomDialog
        title="Are you sure you want to transfer this event?"
        description={`${selectedUser.firstName} ${selectedUser.lastName} will become the requester for "${eventData.title}". You may lose access to this event once the transfer is complete.`}
        primaryButtonText="Yes, transfer this event"
        secondaryButtonText="Back"
        open={true}
        onClose={() => setConfirming(false)}
        onPrimaryButtonClick={handleConfirm}
        onSecondaryButtonClick={() => setConfirming(false)}
        disablePrimaryButton={updating}
      />
    );
  }

  return (
    <Dialog
      open={true}
      onClose={onCancel}
    >
      <DialogTitle>Transfer Event</DialogTitle>
      <DialogContent>
        <Typography>
          Select the person who should take over this event request. Only members
          of the event's program office can be chosen.
        </Typography>
      </DialogContent>
      <Box sx={{ p: 2, pt: 0 }}>
        <FormControl
          fullWidth
          error={Boolean(error)}
        >
          <InputLabel>New Requester</InputLabel>
          <Select
            value={selectedUserId.toString()}
            onChange={handleChange}
            label="New Requester"
          >
            <MenuItem disabled value={0}>Select</MenuItem>
            {candidates.map(user => (
              <MenuItem key={user.id} value={user.id}>
                {user.firstName} {user.lastName} ({user.email})
              </MenuItem>
            ))}
          </Select>
          {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>

        {candidates.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            There is no one else in this event's program office to transfer to.
          </Alert>
        )}

        <Box style={dialogButtons}>
          <Button
            variant="outlined"
            color="primary"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleContinue}
            disabled={updating || candidates.length === 0}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
